<script setup lang="ts">
/**
 * Measurement field — Figma `Sizes · components` 01 / 02. One white row:
 * label left, value + unit right, typed in place. Hover fills it, focus draws
 * the 2px primary frame; an out-of-range value keeps a caption underneath
 * ("Check this value: usually 140–210 cm"). Saving happens on blur / Enter;
 * Escape puts the value back.
 */
import { computed, onMounted, ref, watch } from 'vue'
import { parseFairProfileV2Input } from './fair-profile-v2-measure.data'

interface FairProfileV2MeasurementFieldProps {
  label: string
  /** The value in the shown units; null is empty. */
  value: number | null
  unit: string
  /** The soft warning under the row, when the value is out of the usual range. */
  warning?: string
  disabled?: boolean
  inputId?: string
  /** Focus on mount (quiz step: "the field is empty and focused"). */
  autofocus?: boolean
}

const props = withDefaults(defineProps<FairProfileV2MeasurementFieldProps>(), {
  warning: '',
  disabled: false,
  inputId: undefined,
  autofocus: false,
})

const emit = defineEmits<{
  commit: [raw: string]
  /** Every keystroke — the quiz enables Continue before the field blurs. */
  input: [raw: string]
  focus: []
  blur: []
}>()

const focused = ref(false)
const draft = ref(props.value === null ? '' : String(props.value))
const input = ref<HTMLInputElement | null>(null)

watch(draft, (raw) => emit('input', raw))

onMounted(() => {
  if (props.autofocus) input.value?.focus()
})

watch(
  () => props.value,
  () => {
    if (!focused.value) reset()
  },
)

/** The input hugs its text so the unit sits right after the number. */
const inputWidth = computed(() => `${Math.max(draft.value.length, 1) + 0.5}ch`)

function handleFocus() {
  focused.value = true
  emit('focus')
}

function reset() {
  draft.value = props.value === null ? '' : String(props.value)
}

function handleBlur() {
  focused.value = false
  // Letters or a zero put the old value back instead of reaching the API.
  if (parseFairProfileV2Input(draft.value) === 'invalid') reset()
  else emit('commit', draft.value)
  emit('blur')
}

function handleEscape(event: KeyboardEvent) {
  reset()
  ;(event.target as HTMLInputElement).blur()
}
</script>

<template>
  <div
    class="fair-profile-v2-measure-field"
    :class="{
      'fair-profile-v2-measure-field--focus': focused,
      'fair-profile-v2-measure-field--disabled': disabled,
    }"
  >
    <label
      class="fair-profile-v2-measure-field__row"
      :for="inputId"
    >
      <span class="fair-profile-v2-measure-field__label">{{ label }}</span>
      <span class="fair-profile-v2-measure-field__value">
        <input
          :id="inputId"
          ref="input"
          v-model="draft"
          class="fair-profile-v2-measure-field__input"
          :class="{ 'fair-profile-v2-measure-field__input--empty': !draft }"
          :style="{ width: inputWidth }"
          type="text"
          inputmode="decimal"
          autocomplete="off"
          placeholder="—"
          :disabled="disabled"
          :aria-describedby="warning && inputId ? `${inputId}-hint` : undefined"
          @focus="handleFocus"
          @blur="handleBlur"
          @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
          @keydown.esc.prevent="handleEscape"
        >
        <span class="fair-profile-v2-measure-field__unit">{{ unit }}</span>
      </span>
    </label>
    <p
      v-if="warning"
      :id="inputId ? `${inputId}-hint` : undefined"
      class="fair-profile-v2-measure-field__hint"
    >
      {{ warning }}
    </p>
  </div>
</template>
